import { findIconByAppTypeId, mapRimItemtoArticle } from "@bundles/wheels/data/helpers"
import { RimDetailsArticle } from "@bundles/wheels/data/model"
import { getBundleParams } from "@bundles/wheels/utils"
import { BasketAmountField, Box, Divider, Icon, MuiCheckbox, Stack, styled, Tooltip, Typography, ArticleNumber } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { Article } from "@tm/models"
import { useCallback } from "react"
import { useUser } from "@tm/context-distribution"
import { AccessoryKitArticle } from "./accessoryKitArticle"
import { ErpWrapper } from "./erpWrapper"

const StyledImageBox = styled(Box)({
    margin: ".5em",
    boxShadow: "0 1px 5px rgba(0,0,0,.2)",
})

const StyledNumbersStack = styled(Stack)({
    minWidth: "13.6em",
})

export const AttributesBlock = styled(Stack)(({ theme }) => ({
    backgroundColor: "#f0f0f0",
    borderLeft: `2px solid ${theme.palette.highlight.main}`,
    marginTop: 2,
    flexDirection: "row",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    padding: "4px",
    width: "inherit",
    rowGap: "0.5em",

    "&:first-of-type": {
        marginTop: 0,
    },
}))

type Props = {
    item: RimDetailsArticle
    isSelected: boolean
    onSelectItem: (rim: RimDetailsArticle) => void
    onQuantityChange: (item: RimDetailsArticle, value: number, linkedArticleChanged?: boolean) => void
}

export function RimDetailsArticleV2({ item, isSelected, onSelectItem, onQuantityChange }: Props) {
    const { translateText } = useLocalization()
    const userContext = useUser()?.userContext
    const isLKQ = getBundleParams()?.version === "LKQ"
    const rimPart = mapRimItemtoArticle(item) as Article

    const handleQuantityChange = useCallback((amountItem: number, linkedArticleChanged?: boolean) => {
        onQuantityChange(item, amountItem, linkedArticleChanged)
    }, [])

    const handleSelect = useCallback(() => {
        onSelectItem(item)
    }, [])

    const renderThumbnail = () => {
        return (
            <StyledImageBox>
                <Icon name="axle-car-2" size="extralarge" color="primary" sx={{ margin: ".3em" }} />
            </StyledImageBox>
        )
    }

    const renderNumbers = () => {
        const direction = userContext?.parameter.positionChangeEArtNrHArtNr ? "row-reverse" : "row"

        return (
            <StyledNumbersStack direction={direction} spacing={1} alignItems="center" divider={<Divider orientation="vertical" flexItem />}>
                {rimPart.traderArticleNo && !userContext?.parameter.hideDealerPartNumber && (
                    <ArticleNumber
                        articleNumber={rimPart.traderArticleNo ?? " "}
                        articleNumberType="wholesaler"
                        omitDefaultStyles={userContext?.parameter.positionChangeEArtNrHArtNr}
                    />
                )}
                {rimPart.supplierArticleNo && (
                    <ArticleNumber
                        articleNumber={rimPart.supplierArticleNo ?? " "}
                        articleNumberType="supplier"
                        omitDefaultStyles={userContext?.parameter.positionChangeEArtNrHArtNr}
                    />
                )}
            </StyledNumbersStack>
        )
    }

    const renderDescription = () => {
        return (
            <AttributesBlock divider={<Divider orientation="vertical" flexItem sx={{ margin: "0 1em" }} />}>
                {rimPart.description && <Typography variant="label">{rimPart.description}</Typography>}
                {item.centerBore && <Typography variant="label">{item.centerBore}</Typography>}
                {item.label && <Typography variant="label">{item.label}</Typography>}
            </AttributesBlock>
        )
    }

    const renderIcons = () => {
        return (
            <Stack direction="row" alignItems="center" gap="1em" margin="1em">
                {item.appType && (
                    <Tooltip title={translateText(item.appType)} placement="top">
                        <Icon name={findIconByAppTypeId(item.appTypeID)} color="primary" size="large" />
                    </Tooltip>
                )}
                {isLKQ && (
                    <Tooltip title={translateText(13910)} placement="top" sx={{ visibility: item.brakeRestriction ? "visible" : "hidden" }}>
                        <Icon name="brake-system" size="large" />
                    </Tooltip>
                )}
            </Stack>
        )
    }

    const renderErpInformationAndQuantity = useCallback(() => {
        return (
            <Stack alignItems="center" flexDirection="row">
                <ErpWrapper {...rimPart} />
                <Box sx={{ minWidth: "6em", marginLeft: "1em" }}>
                    <BasketAmountField
                        originalQuantity={rimPart.quantity ?? 1}
                        division={1}
                        onChangeQuantity={(amountItem, loadErpInfo) => handleQuantityChange(amountItem, false)}
                        loadErpInfos
                    />
                </Box>
            </Stack>
        )
    }, [rimPart.quantity])

    return (
        <>
            <Stack direction="row" sx={{ boxShadow: "0 1px 5px rgba(0,0,0,.2)" }}>
                <Stack direction="row" gap={1} flex={1} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" width="100%">
                        {renderThumbnail()}
                        <Stack direction="column" width="100%" padding={1} gap={1}>
                            {renderNumbers()}
                            {renderDescription()}
                        </Stack>
                    </Stack>
                    {renderIcons()}
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} justifyContent="end">
                    <Divider orientation="vertical" flexItem />
                    {renderErpInformationAndQuantity()}
                    <MuiCheckbox
                        checked={isSelected}
                        onChange={handleSelect}
                        sx={isLKQ ? { "& .MuiSvgIcon-root": { fontSize: "2.5em" } } : undefined}
                    />
                </Stack>
            </Stack>
            {isLKQ && item.accessoryKitArticle && (
                <AccessoryKitArticle
                    accessoryKitArticle={item.accessoryKitArticle}
                    onChangeAccessoryKitArticleQuantity={(amountItem) => handleQuantityChange(amountItem, true)}
                />
            )}
        </>
    )
}
